import React, { useEffect, useState } from 'react';
import secureStorage from '../utils/secureStorage';
import { API_BASE_URL } from '../config';
import { CheckCircle, Cloud, Monitor } from 'lucide-react';

const WeatherModule = ({ theme }) => {
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [rssData, setRssData] = useState(null);
  const [view, setView] = useState('dropdown'); // 'dropdown', 'rss', 'manage'
  const [weatherLocations, setWeatherLocations] = useState([]);
  const [editingLocation, setEditingLocation] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form data for CRUD operations
  const [formData, setFormData] = useState({
    city: '',
    state_province: '',
    rss_feed: ''
  });

  // Fetch provinces on component mount
  useEffect(() => {
    fetchProvinces();
    fetchAllLocations();
  }, []);

  const fetchProvinces = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/weather-locations/provinces`);
      if (response.ok) {
        const data = await response.json();
        setProvinces(data);
      } else {
        setError('Failed to fetch provinces');
      }
    } catch (error) {
      console.error('Error fetching provinces:', error);
      setError('Error fetching provinces');
    }
  };

  const fetchCitiesByProvince = async (province) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/weather-locations/cities/${encodeURIComponent(province)}`);
      if (response.ok) {
        const data = await response.json();
        setCities(data);
      } else {
        setError('Failed to fetch cities');
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      setError('Error fetching cities');
    }
  };

  const fetchRSSFeed = async (province, city) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/weather-locations/rss/${encodeURIComponent(province)}/${encodeURIComponent(city)}`);
      if (response.ok) {
        const data = await response.json();
        setRssData(data);
        setView('rss');
      } else {
        setError('Weather data not found for this location');
      }
    } catch (error) {
      console.error('Error fetching RSS feed:', error);
      setError('Error fetching weather data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllLocations = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/weather-locations`);
      if (response.ok) {
        const data = await response.json();
        setWeatherLocations(data);
      }
    } catch (error) {
      console.error('Error fetching all locations:', error);
    }
  };

  const handleProvinceChange = (province) => {
    setSelectedProvince(province);
    setSelectedCity('');
    setCities([]);
    if (province) {
      fetchCitiesByProvince(province);
    }
  };

  const handleCityChange = (city) => {
    setSelectedCity(city);
  };

  const handleGetWeather = () => {
    if (selectedProvince && selectedCity) {
      fetchRSSFeed(selectedProvince, selectedCity);
    }
  };

  const handleBackToDropdown = () => {
    setView('dropdown');
    setRssData(null);
    setError('');
  };

  const handleManageData = () => {
    setView('manage');
    fetchAllLocations();
  };

  const handleAddLocation = () => {
    setFormData({ city: '', state_province: '', rss_feed: '' });
    setEditingLocation(null);
    setShowForm(true);
  };

  const handleEditLocation = (location) => {
    setFormData({
      city: location.city,
      state_province: location.state_province,
      rss_feed: location.rss_feed
    });
    setEditingLocation(location);
    setShowForm(true);
  };

  const handleSaveLocation = async () => {
    try {
      setLoading(true);
      const url = editingLocation 
        ? `${API_BASE_URL}/api/weather-locations/${editingLocation.id}`
        : `${API_BASE_URL}/api/weather-locations`;
      
      const method = editingLocation ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowForm(false);
        fetchAllLocations();
        fetchProvinces();
        alert(editingLocation ? 'Location updated successfully!' : 'Location added successfully!');
      } else {
        setError('Failed to save location');
      }
    } catch (error) {
      console.error('Error saving location:', error);
      setError('Error saving location');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/weather-locations/${locationId}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          fetchAllLocations();
          fetchProvinces();
          alert('Location deleted successfully!');
        } else {
          setError('Failed to delete location');
        }
      } catch (error) {
        console.error('Error deleting location:', error);
        setError('Error deleting location');
      } finally {
        setLoading(false);
      }
    }
  };

  const importSampleData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/weather-locations/import-excel`, {
        method: 'POST'
      });

      if (response.ok) {
        fetchAllLocations();
        fetchProvinces();
        alert('Sample weather data imported successfully!');
      } else {
        setError('Failed to import sample data');
      }
    } catch (error) {
      console.error('Error importing sample data:', error);
      setError('Error importing sample data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      {error && (
        <div className="mb-4 p-4 bg-red-900/20 border border-red-400 text-red-300 rounded">
          {error}
          <button 
            onClick={() => setError('')}
            className="ml-2 text-red-200 hover:text-red-100"
          >
            ×
          </button>
        </div>
      )}

      {/* Dropdown Selection View */}
      {view === 'dropdown' && (
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-3xl font-bold text-orange-500 flex items-center">
              <Cloud className="h-8 w-8 mr-3 text-orange-500" />
              Weather Information
            </h2>
            <div className="space-x-2">
              <button
                onClick={handleManageData}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Manage Data
              </button>
              <button
                onClick={importSampleData}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Import Sample Data
              </button>
            </div>
          </div>

          <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-gray-700">
            <h3 className="text-xl font-bold text-orange-500 text-center mb-6">
              Geostationary Operational Environmental Satellite Images
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Satellite Selection */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  GOES East / West:
                </label>
                <select
                  value={selectedProvince}
                  onChange={(e) => handleProvinceChange(e.target.value)}
                  className="w-full p-3 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                >
                  <option value="">-- GOES East / West --</option>
                  {provinces.map(province => (
                    <option key={province} value={province}>{province}</option>
                  ))}
                </select>
              </div>

              {/* GOES Image Region Selection */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  GOES Image:
                </label>
                <select
                  value={selectedCity}
                  onChange={(e) => handleCityChange(e.target.value)}
                  disabled={!selectedProvince}
                  className="w-full p-3 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white disabled:bg-gray-600 disabled:text-gray-400"
                >
                  <option value="">-- GOES Image --</option>
                  {cities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6 text-center">
              <button
                onClick={handleGetWeather}
                disabled={!selectedProvince || !selectedCity || loading}
                className="px-8 py-3 bg-gray-700 text-white border border-gray-600 rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-600 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Loading...' : 'Get Weather Information'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RSS Display View */}
      {view === 'rss' && rssData && (
        <div className="max-w-4xl mx-auto">
          <div className="mb-6 flex flex-wrap gap-4">
            <button
              onClick={handleBackToDropdown}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              ← Back to Selection
            </button>
            <button
              onClick={async () => {
                try {
                  // Get current user token
                  const token = secureStorage.getToken();
                  
                  if (!token) {
                    alert('Please log in to save weather preference');
                    return;
                  }
                  
                  // Save weather preference to backend
                  const response = await fetch(`${API_BASE_URL}/api/users/weather-preference`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                      city: rssData.city,
                      province: rssData.province,
                      rss_feed: rssData.rss_feed
                    })
                  });
                  
                  if (response.ok) {
                    alert(`Weather location set to ${rssData.city}, ${rssData.province}. Returning to ICS Dashboard...`);
                    // Navigate back to ICS dashboard
                    const exerciseId = new URLSearchParams(window.location.hash.split('?')[1]).get('exercise');
                    window.location.href = `${window.location.origin}${window.location.pathname}#ics?exercise=${exerciseId}`;
                  } else {
                    const error = await response.json();
                    alert(`Failed to save weather preference: ${error.detail || 'Unknown error'}`);
                  }
                } catch (error) {
                  console.error('Error saving weather preference:', error);
                  alert('Failed to save weather preference. Please try again.');
                }
              }}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2"
            >
              <CheckCircle className="h-4 w-4" />
              Set as Dashboard Weather
            </button>
            <button
              onClick={() => window.open('#display-weather', '_blank')}
              className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 flex items-center gap-2"
              title="Open in Display Mode"
            >
              <Monitor className="h-4 w-4" />
              Display Mode
            </button>
            <button
              onClick={handleManageData}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Manage Data
            </button>
          </div>

          <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-gray-700">
            <h3 className="text-2xl font-bold text-orange-500 mb-4">
              Weather Information for {rssData.city}, {rssData.province}
            </h3>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-white mb-2">
                RSS Feed URL:
              </label>
              <div className="flex">
                <input
                  type="text"
                  value={rssData.rss_feed}
                  readOnly
                  className="flex-1 p-3 border border-gray-600 rounded-l-lg bg-gray-700 text-orange-400"
                />
                <button
                  onClick={() => {
                    try {
                      navigator.clipboard.writeText(rssData.rss_feed);
                      alert('RSS Feed URL copied to clipboard!');
                    } catch (err) {
                      alert('Copy failed. Please select and copy the URL manually.');
                    }
                  }}
                  className="px-4 py-3 bg-blue-500 text-white rounded-r-lg hover:bg-blue-600"
                  data-testid="copy-rss-url-btn"
                >
                  Copy
                </button>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Weather Satellite Image:
              </label>
              <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                <img
                  src={rssData.rss_feed}
                  alt={`Weather satellite image for ${rssData.city}, ${rssData.province}`}
                  className="max-w-full h-auto mx-auto"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div className="text-center text-gray-500 py-8" style={{display: 'none'}}>
                  <Cloud className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                  <p>Weather image unavailable</p>
                  <p className="text-sm">The RSS feed may not be accessible or the image format may not be supported.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Management View */}
      {view === 'manage' && (
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-orange-500">Manage Weather Locations</h3>
            <div className="space-x-2">
              <button
                onClick={handleBackToDropdown}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                ← Back to Weather
              </button>
              <button
                onClick={handleAddLocation}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Add Location
              </button>
            </div>
          </div>

          {showForm && (
            <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-gray-700 mb-6">
              <h4 className="text-lg font-bold text-orange-500 mb-4">
                {editingLocation ? 'Edit Location' : 'Add New Location'}
              </h4>
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1">City:</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    className="w-full p-2 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Province/State:</label>
                  <input
                    type="text"
                    value={formData.state_province}
                    onChange={(e) => setFormData({...formData, state_province: e.target.value})}
                    className="w-full p-2 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">RSS Feed URL:</label>
                  <input
                    type="url"
                    value={formData.rss_feed}
                    onChange={(e) => setFormData({...formData, rss_feed: e.target.value})}
                    className="w-full p-2 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveLocation}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          )}

          <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-gray-700">
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      City
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Province/State
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      RSS Feed URL
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-600">
                  {weatherLocations.map((location) => (
                    <tr key={location.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                        {location.city}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {location.state_province}
                      </td>
                      <td className="px-6 py-4 text-sm text-white">
                        <a 
                          href={location.rss_feed} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-orange-400 hover:text-orange-300 truncate block max-w-xs"
                        >
                          {location.rss_feed}
                        </a>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEditLocation(location)}
                          className="text-orange-400 hover:text-orange-300 mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteLocation(location.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {weatherLocations.length === 0 && (
              <div className="text-center py-8 text-white">
                <Cloud className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                <p>No weather locations found</p>
                <p className="text-sm">Add some locations or import sample data to get started</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Weather Observation Module Component

export default WeatherModule;
